import React, { useState, useEffect } from "react";
import { FaMagic, FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import * as aiService from "../../services/aiService";

const Keywords = ({ onNext, data, handlePrevious }) => {
  const [keyword, setKeyword] = useState("");
  const [keywordsList, setKeywordsList] = useState(data?.keywords || []);
  const [productLink, setProductLink] = useState(data?.productLink || "");
  const [loading, setLoading] = useState(false);
  const [suggestingKeywords, setSuggestingKeywords] = useState(false);

  // Sync state when data prop changes (e.g., when navigating back)
  useEffect(() => {
    if (data?.keywords !== undefined) {
      setKeywordsList(data.keywords || []);
    }
    if (data?.productLink !== undefined) {
      setProductLink(data.productLink || "");
    }
  }, [data?.keywords, data?.productLink]);

  const handleAddKeyword = (e) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    if (trimmed && !keywordsList.includes(trimmed)) {
      setKeywordsList([...keywordsList, trimmed]);
      setKeyword("");
    }
  };

  const handleRemoveKeyword = (kw) => {
    setKeywordsList(keywordsList.filter((k) => k !== kw));
  };

  const handleAISuggestKeywords = async () => {
    if (!productLink || productLink.trim().length === 0) {
      toast.info("Please add a product link first for better AI suggestions");
      return;
    }

    setSuggestingKeywords(true);
    try {
      // Extract product description from link or use a generic description
      const productDescription = productLink; // In real app, you might fetch product details
      const suggestedKeywords = await aiService.suggestKeywords(productDescription, "affiliate");
      
      // Add suggested keywords that aren't already in the list
      const newKeywords = suggestedKeywords.filter(kw => !keywordsList.includes(kw.toLowerCase()));
      if (newKeywords.length > 0) {
        setKeywordsList([...keywordsList, ...newKeywords.slice(0, 5)]); // Add top 5
        toast.success(`Added ${Math.min(newKeywords.length, 5)} AI-suggested keywords!`);
      } else {
        toast.info("All suggested keywords are already added");
      }
    } catch (error) {
      console.error("AI keyword suggestion error:", error);
      toast.error("Failed to get AI suggestions. Please try again.");
    } finally {
      setSuggestingKeywords(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (keywordsList.length === 0) {
      alert("Please add at least one keyword");
      return;
    }

    setLoading(true);
    // Pass to next step (Message) - safely trim productLink
    onNext({ 
      keywords: keywordsList, 
      productLink: productLink ? productLink.trim() : "" 
    });
    setLoading(false);
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-6">Keywords</h1>
      <p className="text-gray-600 mb-6">
        Add relevant keywords for your automation. These can help target or trigger specific actions.
      </p>

      {/* Add Keyword Form */}
      <form onSubmit={handleAddKeyword} className="flex items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Enter a keyword..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          Add
        </button>
      </form>

      {/* AI Keyword Suggestions */}
      <div className="mb-6">
        <button
          type="button"
          onClick={handleAISuggestKeywords}
          disabled={suggestingKeywords}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {suggestingKeywords ? (
            <>
              <FaSpinner className="animate-spin" />
              <span>Getting AI suggestions...</span>
            </>
          ) : (
            <>
              <FaMagic />
              <span>Get AI Keyword Suggestions</span>
            </>
          )}
        </button>
        <p className="text-xs text-gray-500 mt-2">
          Let AI analyze your product and suggest effective keywords automatically
        </p>
      </div>

      {/* Keyword List */}
      {keywordsList.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-6">
          {keywordsList.map((kw, index) => (
            <span
              key={index}
              className="flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm"
            >
              {kw}
              <button
                type="button"
                onClick={() => handleRemoveKeyword(kw)}
                className="text-orange-600 hover:text-red-600 font-bold"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm mb-6">No keywords added yet.</p>
      )}

      {/* Product Link Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Product Link (Optional)
        </label>
        <input
          type="url"
          placeholder="Paste product link..."
          value={productLink}
          onChange={(e) => setProductLink(e.target.value)}
          className="w-full border border-gray-300 rounded-md p-2 focus:border-orange-500 focus:ring-orange-500 focus:outline-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          Add a link to your product or service that will be included in the automation
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={handlePrevious}
          className="bg-gray-300 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-400 transition duration-300"
        >
          Back
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className={`px-5 py-2 rounded-md text-white transition duration-300 ${
            loading
              ? "bg-orange-300 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600"
          }`}
        >
          {loading ? "Saving..." : "Next"}
        </button>
      </div>
    </div>
  );
};

export default Keywords;